import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, Star, TrendingUp, Calendar, Award } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Leaderboard = () => {
    const [leaders, setLeaders] = useState([]);
    const [period, setPeriod] = useState('all');
    const [loading, setLoading] = useState(true);
    const [jackpot, setJackpot] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [leadersRes, jackpotRes] = await Promise.all([
                    axios.get(`${API}/leaderboard?period=${period}`, { withCredentials: true }),
                    axios.get(`${API}/jackpot`, { withCredentials: true })
                ]);
                setLeaders(leadersRes.data);
                setJackpot(jackpotRes.data.amount);
            } catch (error) {
                console.error('Error fetching leaderboard:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [period]);

    const getRankIcon = (rank) => {
        switch (rank) {
            case 1:
                return <Crown className="w-6 h-6 text-[#FFD700]" />;
            case 2:
                return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
            case 3:
                return <Medal className="w-6 h-6 text-[#CD7F32]" />;
            default:
                return <span className="w-6 h-6 flex items-center justify-center text-[#A0AAB2] font-bold">#{rank}</span>;
        }
    };

    const getRankStyle = (rank) => {
        switch (rank) {
            case 1:
                return 'bg-gradient-to-r from-[#FFD700]/20 to-transparent border-[#FFD700]/50';
            case 2:
                return 'bg-gradient-to-r from-[#C0C0C0]/20 to-transparent border-[#C0C0C0]/50';
            case 3:
                return 'bg-gradient-to-r from-[#CD7F32]/20 to-transparent border-[#CD7F32]/50';
            default:
                return '';
        }
    };

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />
            
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Jackpot Banner */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-[#FFD700]/10 via-[#FF007F]/10 to-[#00FFFF]/10 border border-[#FFD700]/30 text-center"
                >
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Star className="w-6 h-6 text-[#FFD700]" />
                        <span className="text-[#FFD700] font-bold uppercase tracking-wider">Progressive Jackpot</span>
                        <Star className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <motion.p
                        className="text-5xl sm:text-6xl font-mono font-black text-[#FFD700]"
                        animate={{ scale: [1, 1.02, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        style={{ textShadow: '0 0 30px rgba(255, 215, 0, 0.5)' }}
                        data-testid="jackpot-amount"
                    >
                        ${jackpot.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </motion.p>
                </motion.div>

                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Trophy className="w-8 h-8 text-[#FFD700]" />
                        <h1 className="text-3xl sm:text-4xl font-black text-white">
                            Leader<span className="text-[#FFD700]">board</span>
                        </h1>
                    </div>
                    <p className="text-[#A0AAB2]">Top winners in the slots</p>
                </div>

                {/* Period Tabs */}
                <Tabs defaultValue="all" onValueChange={setPeriod} className="mb-8">
                    <TabsList className="bg-[#121212] border border-white/10 w-full justify-center">
                        <TabsTrigger value="daily" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Daily
                        </TabsTrigger>
                        <TabsTrigger value="weekly" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Weekly
                        </TabsTrigger>
                        <TabsTrigger value="all" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black flex items-center gap-2">
                            <Award className="w-4 h-4" />
                            All Time
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                {/* Leaderboard List */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="spinner" />
                    </div>
                ) : leaders.length === 0 ? (
                    <div className="card-dark p-12 text-center">
                        <Trophy className="w-16 h-16 text-[#A0AAB2] mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">No Leaders Yet</h3>
                        <p className="text-[#A0AAB2]">Start playing to appear on the leaderboard!</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Top 3 Podium */}
                        {leaders.length >= 3 && (
                            <div className="grid grid-cols-3 gap-4 mb-8">
                                {/* 2nd Place */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                    className="card-dark p-4 text-center mt-8 border-[#C0C0C0]/30"
                                >
                                    <Medal className="w-8 h-8 text-[#C0C0C0] mx-auto mb-2" />
                                    <p className="text-white font-bold truncate">{leaders[1]?.user_name}</p>
                                    <p className="text-[#C0C0C0] font-mono font-bold">${leaders[1]?.total_won?.toFixed(2)}</p>
                                </motion.div>
                                
                                {/* 1st Place */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="card-dark p-4 text-center border-[#FFD700]/50 bg-gradient-to-b from-[#FFD700]/10 to-transparent"
                                >
                                    <Crown className="w-10 h-10 text-[#FFD700] mx-auto mb-2" />
                                    <p className="text-white font-bold truncate">{leaders[0]?.user_name}</p>
                                    <p className="text-[#FFD700] font-mono font-bold text-lg">${leaders[0]?.total_won?.toFixed(2)}</p>
                                    <p className="text-xs text-[#A0AAB2] mt-1">Biggest: ${leaders[0]?.biggest_win?.toFixed(2)}</p>
                                </motion.div>
                                
                                {/* 3rd Place */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="card-dark p-4 text-center mt-12 border-[#CD7F32]/30"
                                >
                                    <Medal className="w-8 h-8 text-[#CD7F32] mx-auto mb-2" />
                                    <p className="text-white font-bold truncate">{leaders[2]?.user_name}</p>
                                    <p className="text-[#CD7F32] font-mono font-bold">${leaders[2]?.total_won?.toFixed(2)}</p>
                                </motion.div>
                            </div>
                        )}

                        {/* Rest of the leaderboard */}
                        {leaders.slice(3).map((leader, index) => (
                            <motion.div
                                key={index + 3}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: (index + 3) * 0.05 }}
                                className={`card-dark p-4 flex items-center justify-between ${getRankStyle(leader.rank)}`}
                                data-testid={`leaderboard-rank-${leader.rank}`}
                            >
                                <div className="flex items-center gap-4">
                                    {getRankIcon(leader.rank)}
                                    <div>
                                        <p className="text-white font-medium">{leader.user_name}</p>
                                        <p className="text-[#A0AAB2] text-sm">{leader.games_played} games played</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono font-bold text-[#39FF14]">
                                        ${leader.total_won?.toFixed(2)}
                                    </p>
                                    <p className="text-[#A0AAB2] text-sm">
                                        Best: ${leader.biggest_win?.toFixed(2)}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Leaderboard;
